import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";

const THUMB_MIN = 28;

const useStyles = createUseStyles((theme: any) => ({
    root: {
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    viewport: {
        width: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflowY: "hidden",
        overflowX: "auto",
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": { display: "none" },
    },
    content: {
        height: "100%",
        minWidth: 0,
    },
    bar: {
        position: "relative",
        overflow: "hidden",
        flex: "0 0 auto",
        userSelect: "none",
        // cursor: "pointer",
        alignContent: 'center',
    },
    thumb: {
        borderRadius: 5,
        height: 10,
        background: theme.colors.violet_primary,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
    },
}));

type RenderArgs = {
  renderWidth: number;
  viewportWidth: number;
  isOverflowingX: boolean;
  scrollLeft: number;
};

type CustomHorizontalScrollerProps = {
  plotWidth: number;
  barHeight?: number;
  border?: boolean;
  theme: any;
  children: (args: RenderArgs) => React.ReactNode;
};

export default function CustomHorizontalScroller({
    plotWidth,
    barHeight = 10,
    border = false,
    theme,
    children,
}: CustomHorizontalScrollerProps) {
    const styles = useStyles({ theme });

    const viewportRef = useRef<HTMLDivElement | null>(null);
    const barRef = useRef<HTMLDivElement | null>(null);

    const [viewportWidth, setViewportWidth] = useState(0);
    const [renderWidth, setRenderWidth] = useState(0);
    const [isOverflowingX, setIsOverflowingX] = useState(false);

    const [maxScroll, setMaxScroll] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [thumbWidth, setThumbWidth] = useState(THUMB_MIN);

    const recalc = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport) {return;}

        const vw = viewport.clientWidth;
        setViewportWidth(vw);

        const overflowing = plotWidth > vw;
        setIsOverflowingX(overflowing);

        const nextRenderWidth = Math.max(plotWidth, vw);
        setRenderWidth(nextRenderWidth);

        const nextMaxScroll = Math.max(0, nextRenderWidth - vw);
        setMaxScroll(nextMaxScroll);

        const barW = barRef.current?.clientWidth ?? vw;
        const rawThumbW = nextRenderWidth > 0 ? (vw / nextRenderWidth) * barW : barW;
        const nextThumbW = Math.max(THUMB_MIN, Math.min(barW, rawThumbW));
        setThumbWidth(nextThumbW);

        const clamped = Math.max(0, Math.min(viewport.scrollLeft, nextMaxScroll));
        if (viewport.scrollLeft !== clamped) {viewport.scrollLeft = clamped;}
        setScrollLeft(clamped);
    }, [plotWidth]);

    useLayoutEffect(() => {
        recalc();
    }, [recalc]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {return;}

        const onScroll = () => setScrollLeft(viewport.scrollLeft);
        viewport.addEventListener("scroll", onScroll, { passive: true });

        const ro = new ResizeObserver(() => recalc());
        ro.observe(viewport);
        if (barRef.current) {ro.observe(barRef.current);}

        window.addEventListener("resize", recalc);

        return () => {
            viewport.removeEventListener("scroll", onScroll);
            ro.disconnect();
            window.removeEventListener("resize", recalc);
        };
    }, [recalc]);

    const thumbLeft = (() => {
        const bar = barRef.current;
        if (!bar || maxScroll <= 0) {return 0;}
        const travel = Math.max(0, bar.clientWidth - thumbWidth);

        return (scrollLeft / maxScroll) * travel;
    })();

    const onTrackMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const viewport = viewportRef.current;
        const bar = barRef.current;
        if (!viewport || !bar || maxScroll <= 0) {return;}

        const rect = bar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const travel = Math.max(0, rect.width - thumbWidth);
        const nextThumbLeft = Math.max(0, Math.min(travel, clickX - thumbWidth / 2));
        const ratio = travel > 0 ? nextThumbLeft / travel : 0;
        viewport.scrollLeft = ratio * maxScroll;
    };

    const onThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const viewport = viewportRef.current;
        const bar = barRef.current;
        if (!viewport || !bar || maxScroll <= 0) {return;}

        const rect = bar.getBoundingClientRect();
        const startX = e.clientX;
        const startLeft = thumbLeft;
        const travel = Math.max(0, rect.width - thumbWidth);

        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const nextLeft = Math.max(0, Math.min(travel, startLeft + dx));
            const ratio = travel > 0 ? nextLeft / travel : 0;
            viewport.scrollLeft = ratio * maxScroll;
        };

        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    return (
        <div className={styles.root}>
            <div
                ref={viewportRef}
                className={styles.viewport}
                style={{ overflowX: isOverflowingX ? "auto" : "hidden" }}
            >
                <div className={styles.content} style={{ width: renderWidth }}>
                    {children({ renderWidth, viewportWidth, isOverflowingX, scrollLeft })}
                </div>
            </div>

            <div
                ref={barRef}
                className={styles.bar}
                style={{
                    height: barHeight > 10 ? barHeight : 10,
                    borderTop: border ? `1px solid ${theme.colors.grey_darker}` : '',
                }}
                onMouseDown={onTrackMouseDown}
            >
                <div
                    className={styles.thumb}
                    onMouseDown={onThumbMouseDown}
                    style={{
                        visibility: isOverflowingX ? "visible" : "hidden",
                        pointerEvents: isOverflowingX ? "auto" : "none",
                        width: thumbWidth,
                        transform: `translateX(${thumbLeft}px)`,
                    }}
                />
            </div>
        </div>
    );
}
